/**
 * Performance logging utility for tracking application load times
 * 
 * Usage:
 * 1. Call initializePerfLog() at app startup
 * 2. Enable via tscli flag `isPerfLogEnabled` or URL override ?isPerfLogEnabled=true
 * 3. Import and use setPerfLog with constants:
 * 
 * @example
 * import { setPerfLog, PERF_KEYS, PERF_EVENT } from '/@utils/perf-log';
 * 
 * setPerfLog(PERF_KEYS.LOAD_PINBOARD_SESSION_API, PERF_EVENT.START);
 * setPerfLog(PERF_KEYS.LOAD_PINBOARD_SESSION_API, PERF_EVENT.COMPLETE);
 * 
 * // View results:
 * console.log(window.perfLog);
 * 
 * Design:
 * - setPerfLog() stores data in a flat Map (O(1) operation)
 * - Duplicate calls with the same key+type are silently discarded
 * - window.perfLog getter does ALL the work:
 *   - Builds the hierarchical tree from the flat map
 *   - Applies parent-child relationships based on PERF_PATH_MAP
 *   - Sorts nodes by time
 *   - Returns the complete structured tree
 * - This keeps the hot path (setPerfLog) extremely fast
 */

import { getQueryParamsAndQueryString } from '@thoughtspot/env-flags';
import { isMetricsFloaterEnabled } from '@thoughtspot/session-service';
import { PERF_PATH_MAP } from './constants';
import { captureResourceTimings, findClosestResourceTiming, findOrCreateNode, sortPerfNodes } from './helpers';
import { PerfEventType, PerfKeyType, PerfLogOptions, PerfNode } from './types';

// Re-export types and constants for convenience
export type { PerfNode, ResourceTiming, ResourceMatcher, PerfEventType, PerfKeyType, PerfLogOptions } from './types';
export { PERF_KEYS, PERF_EVENT } from './constants';

// ==================== Module State ====================

// Flat storage: key -> { START?, COMPLETE? }
// All tree building is deferred to the getter
// Keys can be either a PerfKeyType or a composite key like "LOAD_CONTEXT_BOOK_API.viz-123"
interface PerfLogEntry {
    START?: number;
    COMPLETE?: number;
}

const perfLogMap = new Map<string, PerfLogEntry>();
const URL_PARAM_IS_PERF_LOG_ENABLED = 'isPerfLogEnabled';
const URL_PARAM_IS_METRICS_FLOATER_ENABLED = 'isMetricsFloaterEnabled';
// Cached perf log enabled state (null = not yet checked)
// Reset to null in initializePerfLog() so the first real read happens after initFlags()
let cachedPerfLogEnabled: boolean | null = null;

/**
 * Checks if perf logging is enabled (caches result after first check).
 * Reads from tscli flag (isPerfLogEnabled) or metrics floater flag.
 * Cache is invalidated in initializePerfLog() which runs after initFlags().
 */
const checkPerfLogEnabledByUrl = (): boolean => {
    try {
        const queryParams = getQueryParamsAndQueryString().queryParams;
        return (
            queryParams[URL_PARAM_IS_PERF_LOG_ENABLED] === 'true' ||
            queryParams[URL_PARAM_IS_METRICS_FLOATER_ENABLED] === 'true');
    } catch (e) {
        // If anything fails, disable perf logging
    }
    return false;
};
/**
 * Checks if perf logging is enabled (caches result after first check).
 * Reads from tscli flag (isPerfLogEnabled) or metrics floater flag.
 * Cache is invalidated in initializePerfLog() which runs after initFlags().
 */
const checkPerfLogEnabled = (): boolean => {
    if (cachedPerfLogEnabled === null) {
        try {
            cachedPerfLogEnabled =
            (window as any)?.blink?.performanceFlags?.isPerfLogEnabled ===
            'true' || isMetricsFloaterEnabled() || checkPerfLogEnabledByUrl();
        } catch (e) {
            // If anything fails (e.g. called before initFlags), disable perf logging
            cachedPerfLogEnabled = false;
        }
    }
    return cachedPerfLogEnabled;
};

/**
 * Logs a performance event - JUST stores in flat map (O(1))
 * @param key - The event key (must be one of PERF_KEYS constants)
 * @param type - Event type: PERF_EVENT.START or PERF_EVENT.COMPLETE
 * @param options - Optional settings: { trackingId?, val? }
 *                  - trackingId: Tracking ID for multi-instance tracking (creates nested children)
 *                  - val: Custom timestamp (defaults to performance.now())
 * 
 * @example
 * import { setPerfLog, PERF_KEYS, PERF_EVENT } from '/@utils/perf-log';
 * 
 * // Simple usage
 * setPerfLog(PERF_KEYS.APP_INIT, PERF_EVENT.START);
 * setPerfLog(PERF_KEYS.LOAD_PINBOARD_SESSION_API, PERF_EVENT.COMPLETE);
 * 
 * // With custom timestamp
 * setPerfLog(PERF_KEYS.DOCUMENT_PARSE, PERF_EVENT.COMPLETE, { val: perfMeta.docParseEnd });
 * 
 * // For multiple widgets, pass a trackingId to track each separately:
 * setPerfLog(PERF_KEYS.LOAD_CONTEXT_BOOK_API, PERF_EVENT.START, { trackingId: 'viz-123' });
 * setPerfLog(PERF_KEYS.LOAD_CONTEXT_BOOK_API, PERF_EVENT.COMPLETE, { trackingId: 'viz-123' });
 */
export const setPerfLog = (key: PerfKeyType, type: PerfEventType, options?: PerfLogOptions) => {
    try {
        if (!checkPerfLogEnabled()) {
            return;
        }
        // Ignore if key is not in PERF_PATH_MAP
        if (!(key in PERF_PATH_MAP)) {
            return;
        }

        const { trackingId, val } = options ?? {};

        // Early exit if trackingId key is explicitly provided but value is null/undefined
        if (options && 'trackingId' in options && !trackingId) {
            return;
        }

        // Create composite key when trackingId is provided (e.g., "LOAD_CONTEXT_BOOK_API.viz-123")
        const storageKey = trackingId ? `${key}.${trackingId}` : key;

        let entry = perfLogMap.get(storageKey);
        if (!entry) {
            entry = {};
            perfLogMap.set(storageKey, entry);
        }

        // Only set value if not already set (discard duplicate updates for same key+trackingId+type)
        if (entry[type] === undefined) {
            entry[type] = val ?? performance.now();
            
            // Also set performance marker
            try {
                const markerName = `${storageKey}--${type}`;
                performance.mark(markerName);
            } catch (e) {
                // Silently fail if performance API is not available
            }
        }
        // If value already exists, silently discard this update
    } catch (e) {
        // Silently fail if performance API is not available
    }
};

/**
 * Builds the hierarchical tree from the flat perfLogMap
 * This does ALL the work: tree building, parent-child relationships, sorting
 * Called only when window.perfLog is accessed
 * 
 * Handles both regular keys (e.g., "APP_INIT") and composite keys with
 * trackingIds (e.g., "LOAD_CONTEXT_BOOK_API.viz-123") to create nested child nodes.
 */
const buildTreeFromMap = (): PerfNode[] => {
    const tree: PerfNode[] = [];
    
    // Iterate through all logged entries in the flat map
    Array.from(perfLogMap.entries()).forEach(([storageKey, entry]) => {
        // Check if this is a composite key with a trackingId (e.g., "LOAD_CONTEXT_BOOK_API.viz-123")
        const dotIndex = storageKey.indexOf('.');
        const baseKey = dotIndex !== -1 ? storageKey.substring(0, dotIndex) : storageKey;
        const trackingId = dotIndex !== -1 ? storageKey.substring(dotIndex + 1) : null;
        
        // Resolve the parent path for the base key from PERF_PATH_MAP
        let parentPath: string = PERF_PATH_MAP[baseKey as PerfKeyType];
        
        // Handle $trackingId placeholder in parent path
        // This allows trackingId to be inserted INTO the parent path instead of appended at the end
        // e.g., "PINBOARD_VIZ_CONTAINER_VIEWPORT.$trackingId" + trackingId "vizId" 
        //       -> "PINBOARD_VIZ_CONTAINER_VIEWPORT.vizId.VIZ_RENDER_QUEUE_PRIMARY"
        const hasTrackingIdPlaceholder = parentPath?.includes('$trackingId');
        if (hasTrackingIdPlaceholder && trackingId) {
            parentPath = parentPath.replace('$trackingId', trackingId);
        }
        
        // Build the full path: parentPath + baseKey + trackingId (if no placeholder)
        let fullPathParts: string[];
        if (parentPath) {
            fullPathParts = parentPath.split('.').concat(baseKey);
        } else {
            fullPathParts = [baseKey];
        }
        
        // If there's a trackingId and no placeholder was used, add it as a child node under the base key
        if (trackingId && !hasTrackingIdPlaceholder) {
            fullPathParts.push(trackingId);
        }
        
        // Find or create the node in the tree (builds parent-child structure)
        const node = findOrCreateNode(tree, fullPathParts);
        
        // Copy the START and COMPLETE values to the node, converting to seconds
        if (entry.START !== undefined) {
            node.START = parseFloat((entry.START / 1000).toFixed(3));
        }
        if (entry.COMPLETE !== undefined) {
            node.COMPLETE = parseFloat((entry.COMPLETE / 1000).toFixed(3));
        }
    });
    
    return tree;
};


/**
 * Returns this viz's render order (1, 2, 3...).
 * The counter resets whenever `sessionKey` changes, so the caller controls the
 * scope: passing "<load>:<tabId>" makes the order reset on both a new liveboard
 * load and a tab switch, i.e. it counts render order within the viewed tab.
 */
let vizRenderOrderKey: string | null = null;
let vizRenderOrder = 0;

export const getNextVizRenderOrder = (sessionKey: string): number => {
    if (sessionKey !== vizRenderOrderKey) {
        vizRenderOrderKey = sessionKey;
        vizRenderOrder = 0;
    }
    vizRenderOrder += 1;
    return vizRenderOrder;
};

// ==================== Navigation Type ====================

// Flips to true on the first in-app (SPA) route change. Lets us tell a direct
// load (app booted straight into the liveboard) from a warm in-app navigation.
let hasNavigatedInApp = false;

export const markInAppNavigation = (): void => {
    hasNavigatedInApp = true;
};

/**
 * Was this liveboard reached by a direct load (deep link, typed URL, refresh)
 */
export const isDirectNavigation = (): boolean => {
    try {
        const nav = performance.getEntriesByType('navigation')[0] as
            | PerformanceNavigationTiming
            | undefined;
        const type = nav?.type ?? '';
        return (type === 'navigate' || type === 'reload') && !hasNavigatedInApp;
    } catch (e) {
        return false;
    }
};

let initialTabSessionKey: string | null = null;
let initialTabId: string | undefined;

export const captureInitialTab = (
    sessionKey: string,
    tabId?: string,
): void => {
    if (!sessionKey) return;
    if (sessionKey !== initialTabSessionKey) {
        initialTabSessionKey = sessionKey;
        initialTabId = tabId || undefined;
    } else if (!initialTabId && tabId) {
        initialTabId = tabId;
    }
};

export const isTabSwitched = (
    sessionKey: string,
    activeTabId?: string,
): boolean => {
    if (sessionKey !== initialTabSessionKey) return false;
    return !!initialTabId && !!activeTabId && activeTabId !== initialTabId;
};

// ==================== First Viewport ====================

// Tracks which vizzes became renderable as part of the initial view (before the
// user scrolled) vs. were revealed by scrolling down. 
//
// Keyed by pinboardSessionId so it resets by itself on each new load.
let firstViewportSessionKey: string | null = null;
let hasLiveboardScrolled = false;
const firstViewportVizIds = new Set<string>();

// Reset the tracking when a new liveboard load is seen.
const ensureFirstViewportSession = (sessionKey: string): void => {
    if (sessionKey !== firstViewportSessionKey) {
        firstViewportSessionKey = sessionKey;
        hasLiveboardScrolled = false;
        firstViewportVizIds.clear();
    }
};

// Call on the first user scroll of the liveboard. Vizzes that render after this
// are treated as scroll-revealed (not in the first viewport).
export const markLiveboardScrolled = (sessionKey: string): void => {
    ensureFirstViewportSession(sessionKey);
    hasLiveboardScrolled = true;
};

// Call when a viz's content becomes renderable. Records it as first-viewport
// only if the user has not scrolled yet.
export const recordVizViewportEntry = (
    sessionKey: string,
    vizId: string,
): void => {
    ensureFirstViewportSession(sessionKey);
    if (vizId && !hasLiveboardScrolled) {
        firstViewportVizIds.add(vizId);
    }
};

// True if the viz was in the initial viewport (rendered before any scroll).
export const isVizInFirstViewport = (vizId: string): boolean =>
    firstViewportVizIds.has(vizId);


// True if the viz was NOT in the initial viewport
export const isExtraVizLoaded = (vizId: string): boolean =>
    !isVizInFirstViewport(vizId);

// ==================== Mixpanel Extractors ====================

/**
 * Defers a function to browser idle time to avoid blocking the main thread.
 * Falls back to setTimeout(fn, 0) on browsers without requestIdleCallback (Safari).
 */
export const scheduleWhenIdle = (fn: () => void): void => {
    if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(fn, { timeout: 5000 });
    } else {
        setTimeout(fn, 0);
    }
};

/**
 * Converts a SCREAMING_SNAKE_CASE key (with optional _start/_duration/_complete suffix)
 * to camelCase for Mixpanel property naming.
 * e.g. "APP_INIT_start" -> "appInitStart"
 */
const toCamelCase = (key: string): string =>
    key
        .toLowerCase()
        .split('_')
        .map((word, i) => (i === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
        .join('');

/**
 * Returns a flat Mixpanel-ready property map for all liveboard-level (non-per-viz) keys.
 * Reads directly from perfLogMap — no tree building, no sorting.
 *
 * Property naming:
 *   - Both START + COMPLETE  →  KEY_duration  (seconds)
 *   - START only             →  KEY_start     (seconds from navigation start)
 *   - COMPLETE only          →  KEY_complete  (seconds from navigation start)
 *
 * Also includes network resource timings from the Performance API.
 * Returns an empty object if perf logging was not enabled.
 *
 * MUST be called inside scheduleWhenIdle() — do not call on the main thread.
 */
export const getLiveboardPerfBreakdown = (): Record<string, number> => {
    const result: Record<string, number> = {};
    if (!perfLogMap.size) return result;

    perfLogMap.forEach((entry, storageKey) => {
        // Skip per-viz composite keys (e.g. "LOAD_CONTEXT_BOOK_API.viz-123")
        if (storageKey.includes('.')) return;

        if (entry.START !== undefined) {
            result[toCamelCase(`${storageKey}_start`)] = Math.round(entry.START);
        }
        if (entry.START !== undefined && entry.COMPLETE !== undefined) {
            result[toCamelCase(`${storageKey}_duration`)] = Math.round(entry.COMPLETE - entry.START);
        } else if (entry.COMPLETE !== undefined) {
            result[toCamelCase(`${storageKey}_complete`)] = Math.round(entry.COMPLETE);
        }
    });

    // Network timings from Performance API (RESOURCE_MATCHERS) — flattened as individual properties
    const resourceTimings = captureResourceTimings();
    Object.entries(resourceTimings).forEach(([key, timing]) => {
        const prefix = toCamelCase(key);
        result[`${prefix}Start`] = timing.start;
        result[`${prefix}Duration`] = timing.duration;
        result[`${prefix}Ssl`] = timing.ssl;
        result[`${prefix}Stalled`] = timing.stalled;
        result[`${prefix}Queueing`] = timing.queueing;
    });

    return result;
};

const CRITICAL_LIVEBOARD_PERF_KEYS: string[] = [
    'DOCUMENT_PARSE',
    'APP_INIT',
    'APP_ROOT',
    'APP_VIEW',
    'AUTHENTICATED_APP_LAYOUT',
    'PINBOARD_PAGE_START',
    'PINBOARD_EMBED_PAGE_START',
    'PINBOARD_CONTENT_CONTAINER',
    'LOAD_PINBOARD_SESSION_API',
];

export const getCriticalLiveboardPerfMetrics = (): Record<string, number> => {
    const result: Record<string, number> = {};
    if (!perfLogMap.size) return result;

    CRITICAL_LIVEBOARD_PERF_KEYS.forEach(key => {
        const entry = perfLogMap.get(key);
        if (!entry) return;
        if (entry.START !== undefined) {
            result[toCamelCase(`${key}_start`)] = Math.round(entry.START);
        }
        if (entry.START !== undefined && entry.COMPLETE !== undefined) {
            result[toCamelCase(`${key}_duration`)] = Math.round(
                entry.COMPLETE - entry.START,
            );
        } else if (entry.COMPLETE !== undefined) {
            result[toCamelCase(`${key}_complete`)] = Math.round(entry.COMPLETE);
        }
    });

    return result;
};

/**
 * Returns a flat Mixpanel-ready property map for a single viz's lifecycle keys.
 * Reads composite keys of the form "BASE_KEY.{vizId}" from perfLogMap.
 *
 * @param vizId - the trackingId used in setPerfLog calls (container.id)
 *
 * MUST be called inside scheduleWhenIdle() — do not call on the main thread.
 */
export const getVizPerfBreakdown = (vizId: string): Record<string, number> => {
    const result: Record<string, number> = {};
    if (!perfLogMap.size || !vizId) return result;

    perfLogMap.forEach((entry, storageKey) => {
        const dotIndex = storageKey.indexOf('.');
        if (dotIndex === -1) return;
        const trackingId = storageKey.slice(dotIndex + 1);
        const baseKey = storageKey.slice(0, dotIndex);
        // Match exact vizId OR vizId with a uniqueId suffix (e.g. "abc123-1" from _.uniqueId)
        const normalizedId = trackingId.replace(/-\d+$/, '');
        if (normalizedId !== vizId) return;

        if (entry.START !== undefined) {
            result[toCamelCase(`${baseKey}_start`)] = Math.round(entry.START);
        }
        if (entry.START !== undefined && entry.COMPLETE !== undefined) {
            result[toCamelCase(`${baseKey}_duration`)] = Math.round(entry.COMPLETE - entry.START);
        } else if (entry.COMPLETE !== undefined) {
            result[toCamelCase(`${baseKey}_complete`)] = Math.round(entry.COMPLETE);
        }
    });

    // Correlate LoadContextBook network timing for this viz by finding the
    // PerformanceResourceTiming entry closest to this viz's LOAD_CONTEXT_BOOK_API start time
    const contextBookEntry = perfLogMap.get(`LOAD_CONTEXT_BOOK_API.${vizId}`);
    if (contextBookEntry?.START !== undefined) {
        const networkTiming = findClosestResourceTiming('op=LoadContextBook', contextBookEntry.START);
        if (networkTiming) {
            result.loadContextBookApiNetworkStart = networkTiming.start;
            result.loadContextBookApiNetworkDuration = networkTiming.duration;
            result.loadContextBookApiNetworkSsl = networkTiming.ssl;
            result.loadContextBookApiNetworkStalled = networkTiming.stalled;
            result.loadContextBookApiNetworkQueueing = networkTiming.queueing;
        }
    }

    return result;
};

// ==================== Public API ====================

/**
 * Initializes the performance logging system
 * Only active when isPerfLogEnabled=true is in the URL
 * Uses Object.defineProperty getter to build and return the tree on-demand
 */
export const initializePerfLog = () => {
    try {
        // Reset cache so checkPerfLogEnabled re-reads from flags now that initFlags() has run.
        // Without this, any setPerfLog calls before initializePerfLog would cache false permanently.
        cachedPerfLogEnabled = null;

        // Always expose setPerfLog globally — viz-render-queue (ts-packages) calls it via window
        // since it can't import from blink-v2 directly
        (window as any).setPerfLog = setPerfLog;

        if (checkPerfLogEnabled()) {
            // Expand the resource timing buffer so API calls aren't dropped when the buffer fills.
            // Called here (before liveboard navigation) so LoadPinboardSession and LoadContextBook
            // are captured. The dynamic listener handles edge cases where 1000 isn't enough.
            try {
                performance.setResourceTimingBufferSize(1000);
            } catch (e) {
                // Silently fail if not supported
            }

            // Define perfLog as a property with a getter that builds the tree on-demand
            Object.defineProperty(window, 'perfLog', {
                get() {
                    try {
                        // Build the tree from the flat map (all tree logic happens here)
                        const tree = buildTreeFromMap();
                        
                        // Capture resource timings and add to tree
                        const resourceTimings = captureResourceTimings();
                        Object.entries(resourceTimings).forEach(([key, timing]) => {
                            tree.push({
                                key,
                                START: timing.start/1000,
                                COMPLETE: timing.complete/1000,
                                steps: [],
                            });
                        });
                        
                        // Sort the tree by START time (fallback to COMPLETE time)
                        // All transformation happens here in the getter
                        return sortPerfNodes(tree);
                    } catch (e) {
                        // Silently fail and return empty array
                        return [];
                    }
                },
                configurable: true,
            });
            
            // Handle docParseEnd from perfMeta if available
            if ((window as any).perfMeta?.docParseEnd) {
                setPerfLog('DOCUMENT_PARSE', 'COMPLETE', { val: (window as any).perfMeta.docParseEnd });
            }
        }
    } catch (e) {
        // Silently fail if initialization fails
    }
};
